import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Users, X, CheckSquare, Plus } from 'lucide-react';
import { format, startOfToday } from 'date-fns';
import { usersApi, User, request } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useTelegram } from '../hooks/useTelegram';

type ChecklistItem = {
  text: string;
  plannedDate: string;
  dueAt: string;
  requiresApproval: boolean;
};

export function AssignChecklistPage() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId?: string }>();
  const { hapticFeedback, showAlert } = useTelegram();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<'select-users' | 'create-items'>('select-users');
  const [selectedAssignees, setSelectedAssignees] = useState<User[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [approverId, setApproverId] = useState<string>('');

  // Получаем подчиненных
  const { data: subordinates, isLoading: subordinatesLoading } = useQuery({
    queryKey: ['subordinates'],
    queryFn: () => usersApi.getSubordinates(),
  });

  // Получаем пользователей для выбора приёмщиков
  const { data: allUsers } = useQuery({
    queryKey: ['users', 'for-approvers'],
    queryFn: () => usersApi.search(''),
  });

  const addAssignee = (user: User) => {
    if (!selectedAssignees.find(u => u.id === user.id)) {
      setSelectedAssignees([...selectedAssignees, user]);
      hapticFeedback('light');
    }
  };

  const removeAssignee = (userId: string) => {
    setSelectedAssignees(selectedAssignees.filter(u => u.id !== userId));
    hapticFeedback('light');
  };

  const addItem = () => {
    const today = startOfToday();
    setItems([
      ...items,
      {
        text: '',
        plannedDate: format(today, 'yyyy-MM-dd\'T\'HH:mm'),
        dueAt: format(today, 'yyyy-MM-dd\'T\'HH:mm'),
        requiresApproval: true,
      },
    ]);
    hapticFeedback('light');
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
    hapticFeedback('light');
  };

  const updateItem = (index: number, field: keyof ChecklistItem, value: any) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!title.trim()) {
        throw new Error('Укажите название чек-листа');
      }
      if (selectedAssignees.length === 0) {
        throw new Error('Выберите хотя бы одного сотрудника');
      }
      if (items.length === 0) {
        throw new Error('Добавьте хотя бы одно дело');
      }
      if (items.some(item => !item.text.trim())) {
        throw new Error('Заполните текст для всех дел');
      }

      // Используем новый эндпоинт для создания чек-листа с делами
      return request<any>('/checklist-assignments/create-with-items', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || undefined,
          projectId: projectId || undefined,
          assigneeIds: selectedAssignees.map(u => u.id),
          items: items.map(item => ({
            text: item.text.trim(),
            plannedDate: new Date(item.plannedDate).toISOString(),
            dueAt: new Date(item.dueAt).toISOString(),
            requiresApproval: item.requiresApproval,
          })),
          approverId: approverId || undefined,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      hapticFeedback('success');
      showAlert('Чек-лист создан и назначен сотрудникам!');
      navigate('/checklists');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось создать чек-лист');
    },
  });

  const handleNext = () => {
    if (selectedAssignees.length === 0) {
      showAlert('Выберите хотя бы одного сотрудника');
      return;
    }
    setStep('create-items');
    hapticFeedback('light');
  };

  const handleBack = () => {
    setStep('select-users');
    hapticFeedback('light');
  };

  const handleSubmit = () => {
    hapticFeedback('medium');
    createMutation.mutate();
  };

  // Шаг 1: Выбор сотрудников
  if (step === 'select-users') {
    return (
      <div className="p-4 space-y-4 pb-24">
        <div>
          <h1 className="text-2xl font-bold text-tg-text flex items-center gap-2">
            <UserPlus className="w-6 h-6" />
            Создать чек-лист подчинённому
          </h1>
          <p className="text-sm text-tg-hint mt-1">
            Шаг 1: Выберите сотрудников
          </p>
        </div>

        {/* Выбор сотрудников */}
        <Card className="p-4">
          <h2 className="text-lg font-semibold text-tg-text mb-3 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Сотрудники ({selectedAssignees.length})
          </h2>

          {selectedAssignees.length > 0 && (
            <div className="space-y-2 mb-3">
              {selectedAssignees.map((assignee) => (
                <div
                  key={assignee.id}
                  className="flex items-center justify-between p-2 bg-blue-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    {assignee.photoUrl ? (
                      <img
                        src={assignee.photoUrl}
                        alt={assignee.firstName}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                        {assignee.firstName[0]}{assignee.lastName?.[0] || ''}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-tg-text">
                        {assignee.lastName || ''} {assignee.firstName}
                      </p>
                      {assignee.username && (
                        <p className="text-xs text-tg-hint">@{assignee.username}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removeAssignee(assignee.id)}
                    className="p-1 text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {subordinatesLoading ? (
            <div className="text-center py-4 text-tg-hint text-sm">Загрузка...</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {subordinates
                ?.filter((u: User) => !selectedAssignees.find((sa: User) => sa.id === u.id))
                .map((user: User) => (
                  <button
                    key={user.id}
                    onClick={() => addAssignee(user)}
                    className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-left"
                  >
                    {user.photoUrl ? (
                      <img
                        src={user.photoUrl}
                        alt={user.firstName}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white text-sm font-semibold">
                        {user.firstName[0]}{user.lastName?.[0] || ''}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-tg-text">
                        {user.lastName || ''} {user.firstName}
                      </p>
                      {user.username && (
                        <p className="text-xs text-tg-hint">@{user.username}</p>
                      )}
                    </div>
                    <div className="text-blue-500">
                      <UserPlus className="w-5 h-5" />
                    </div>
                  </button>
                ))}
            </div>
          )}

          {subordinates && subordinates.length === 0 && (
            <div className="text-center py-4 text-tg-hint text-sm">
              Нет доступных подчиненных
            </div>
          )}
        </Card>

        {/* Кнопка "Далее" */}
        <div className="sticky bottom-0 bg-white pt-4 pb-4 border-t">
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={selectedAssignees.length === 0}
            className="w-full"
          >
            Далее
          </Button>
        </div>
      </div>
    );
  }

  // Шаг 2: Создание дел
  return (
    <div className="p-4 space-y-4 pb-24">
      <div>
        <h1 className="text-2xl font-bold text-tg-text flex items-center gap-2">
          <CheckSquare className="w-6 h-6" />
          Создать чек-лист подчинённому
        </h1>
        <p className="text-sm text-tg-hint mt-1">
          Шаг 2: Создайте дела для чек-листа
        </p>
      </div>

      {/* Информация о выбранных сотрудниках */}
      <Card className="p-4 bg-blue-50">
        <p className="text-sm text-tg-text">
          <strong>Выбрано сотрудников:</strong> {selectedAssignees.length}
        </p>
        <p className="text-xs text-tg-hint mt-1">
          {selectedAssignees.map(u => `${u.lastName || ''} ${u.firstName}`).join(', ')}
        </p>
      </Card>

      {/* Название и описание */}
      <Card className="p-4">
        <Input
          label="Название чек-листа"
          value={title}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
          placeholder="Введите название"
        />
        <div className="mt-3">
          <label className="text-sm font-medium text-tg-text mb-2 block">Описание (необязательно)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Введите описание"
            className="w-full p-3 border rounded-lg text-sm"
            rows={2}
          />
        </div>
      </Card>

      {/* Дела */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-tg-text">Дела</h2>
          <Button size="sm" variant="secondary" onClick={addItem}>
            <Plus className="w-4 h-4 mr-1" />
            Добавить дело
          </Button>
        </div>

        <div className="space-y-4">
          {items.map((item, index) => (
            <Card key={index} className="p-4 bg-gray-50">
              <div className="flex items-start justify-between mb-3">
                <span className="text-sm font-medium text-tg-text">Дело {index + 1}</span>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => removeItem(index)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3">
                <Input
                  label="Текст дела"
                  value={item.text}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(index, 'text', e.target.value)}
                  placeholder="Введите текст дела"
                />

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-tg-text mb-2 block">Планируемая дата</label>
                    <input
                      type="datetime-local"
                      value={item.plannedDate}
                      onChange={(e) => updateItem(index, 'plannedDate', e.target.value)}
                      className="w-full p-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-tg-text mb-2 block">Срок выполнения</label>
                    <input
                      type="datetime-local"
                      value={item.dueAt}
                      onChange={(e) => updateItem(index, 'dueAt', e.target.value)}
                      min={item.plannedDate}
                      className="w-full p-2 border rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={item.requiresApproval}
                    onChange={(e) => updateItem(index, 'requiresApproval', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <label className="text-sm text-tg-text">Требует приёмки</label>
                </div>
              </div>
            </Card>
          ))}

          {items.length === 0 && (
            <div className="text-center py-8 text-tg-hint">
              <CheckSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Добавьте дела для чек-листа</p>
            </div>
          )}
        </div>
      </Card>

      {/* Приёмщик (опционально) */}
      <Card className="p-4">
        <h2 className="text-lg font-semibold text-tg-text mb-3">
          Приёмщик (необязательно)
        </h2>
        <select
          value={approverId}
          onChange={(e) => setApproverId(e.target.value)}
          className="w-full p-3 border rounded-lg text-sm"
        >
          <option value="">Не указан</option>
          {allUsers?.map((user: User) => (
            <option key={user.id} value={user.id}>
              {user.lastName || ''} {user.firstName}
            </option>
          ))}
        </select>
      </Card>

      {/* Кнопки действий */}
      <div className="flex gap-2 sticky bottom-0 bg-white pt-4 pb-4 border-t">
        <Button
          variant="secondary"
          onClick={handleBack}
          className="flex-1"
        >
          Назад
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={createMutation.isPending || !title.trim() || items.length === 0}
          className="flex-1"
        >
          {createMutation.isPending ? 'Создание...' : 'Создать'}
        </Button>
      </div>
    </div>
  );
}
